import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Droplets, ShieldCheck } from "lucide-react";
import AddressFields from "@/components/AddressFields";

const HERO_IMG = "https://images.unsplash.com/photo-1562016600-ece13e8ba570?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

function GoogleGlyph() {
  // Official Google "G" multi-color mark used on "Sign in with Google" buttons.
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" className="mr-2" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.8 32.8 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.1 8 3l5.7-5.7C34 6.3 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3.1 0 5.9 1.1 8 3l5.7-5.7C34 6.3 29.3 4 24 4c-7.7 0-14.4 4.4-17.7 10.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.1c-2 1.4-4.5 2.3-7.2 2.3-5.4 0-9.8-3.2-11.3-7.9l-6.5 5C9.4 39.5 16.1 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2.1 3.7-3.9 4.9l6.2 5.1C40.9 35 44 30 44 24c0-1.2-.1-2.4-.4-3.5z"/>
    </svg>
  );
}

export default function LoginPage() {
  const { user: me, login, verifyLoginCode, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);

  // login form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // new-device verification step
  const [challenge, setChallenge] = useState(null); // {challenge_id, email_hint}
  const [code, setCode] = useState("");
  const [resendIn, setResendIn] = useState(0); // seconds until resend allowed

  // Already signed in? Send them home (replace) so phone "back" can't land here.
  useEffect(() => {
    if (!me) return;
    const home =
      me.role === "admin" || me.role === "secretary" ? "/admin" :
      me.role === "driver" ? "/driver" :
      me.role === "worker" ? "/worker" :
      me.role === "mgmt" ? "/managementusa/admin" :
      "/portal";
    navigate(home, { replace: true });
  }, [me, navigate]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  // register form
  const [reg, setReg] = useState({
    name: "", business_name: "", llc_name: "", billing_address: "",
    billing_city: "", billing_state: "", billing_zip: "",
    secondary_invoice_email: "",
    contact_phone: "", billing_phone: "", contact_email: "", email: "", password: "",
  });
  const setR = (k, v) => setReg((p) => ({ ...p, [k]: v }));
  const setRegMany = (patch) => setReg((p) => ({ ...p, ...patch }));

  const goHome = (user) => {
    toast.success(`Welcome back, ${user.name || user.email}`);
    const home =
      user.role === "admin" ? "/admin" :
      user.role === "secretary" ? "/admin" :
      user.role === "driver" ? "/driver" :
      user.role === "worker" ? "/worker" :
      "/portal";
    navigate(home, { replace: true });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) goHome(res.user);
    else if (res.challenge) {
      setChallenge(res.challenge);
      setCode("");
      setResendIn(30);
      toast.message(`We emailed a verification code to ${res.challenge.email_hint}`);
    } else toast.error(res.error);
  };

  const handleResend = async () => {
    setLoading(true);
    const res = await login(email, password); // re-running login issues a fresh code
    setLoading(false);
    if (res.challenge) {
      setChallenge(res.challenge);
      setCode("");
      setResendIn(30);
      toast.success(`A new code was sent to ${res.challenge.email_hint}`);
    } else if (res.ok) goHome(res.user);
    else toast.error(res.error);
  };

  const handleVerifyCode = async (e, overrideCode) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const useCode = (overrideCode ?? code).trim();
    if (!useCode) return toast.error("Enter the code from your email");
    setLoading(true);
    const res = await verifyLoginCode(challenge.challenge_id, useCode);
    setLoading(false);
    if (res.ok) goHome(res.user);
    else toast.error(res.error);
  };

  // Paste-to-sign-in: if the user pastes a code that looks complete, fill the
  // input AND fire verification immediately so they don't have to also press
  // Enter or click the button. Enter still works for keyboard typists.
  const handleCodePaste = (e) => {
    const pasted = (e.clipboardData?.getData("text") || "").trim();
    if (!pasted || pasted.length < 6 || pasted.length > 12) return;
    if (loading) return;
    e.preventDefault();
    setCode(pasted);
    handleVerifyCode(null, pasted);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      ...reg,
      contact_email: reg.contact_email || undefined,
      secondary_invoice_email: reg.secondary_invoice_email || undefined,
    };
    const res = await register(payload);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome! Your account is ready.");
      navigate("/portal");
    } else toast.error(res.error);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
  // THIS BREAKS THE AUTH. The redirect must be derived from the browser's
  // current origin so OAuth comes back to the exact domain the user is on
  // (preview vs production).
  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + "/auth-callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left hero */}
      <div className="relative hidden md:block">
        <img src={HERO_IMG} alt="Pool water" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-foreground/40" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-baseline gap-1">
            <Droplets className="w-6 h-6 mr-2" />
            <span className="text-2xl font-black tracking-tighter font-display">CATSKILLS</span>
            <span className="text-lg font-light tracking-wide">POOL</span>
          </div>
          <div>
            <p className="overline text-white/70 mb-4">Bulk Chlorine · Delivered</p>
            <h1 className="font-display text-5xl font-black tracking-tighter leading-none">
              Cleaner pools.<br />
              Tighter routes.<br />
              Faster invoices.
            </h1>
            <p className="mt-6 text-white/80 max-w-md leading-relaxed">
              The operating system for Catskills Pool Supplies LLC — from delivery ticket to paid invoice in a single tap.
            </p>
          </div>
          <div className="text-xs text-white/60 font-mono">© Catskills Pool Supplies LLC</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="md:hidden flex items-baseline gap-1 mb-6">
            <span className="text-2xl font-black tracking-tighter text-primary font-display">CATSKILLS</span>
            <span className="text-lg font-light tracking-wide">POOL</span>
          </div>
          <p className="overline mb-2">Account</p>
          <h2 className="font-display text-3xl font-bold tracking-tight mb-8">Sign in to continue</h2>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full rounded-sm">
              <TabsTrigger value="login" data-testid="tab-login">Log in</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Customer Sign-up</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              {challenge ? (
              <form onSubmit={handleVerifyCode} className="space-y-4 mt-6" data-testid="login-code-form">
                <div className="border border-primary/40 bg-primary/5 rounded-sm p-4">
                  <p className="font-bold text-sm text-primary flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> New device check</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    We don&apos;t recognize this device, so we emailed a one-time code to <b>{challenge.email_hint}</b>.
                    The code is <b>case-sensitive</b> and stops working after 2 wrong tries.
                  </p>
                </div>
                <div>
                  <Label htmlFor="logincode">Verification code</Label>
                  <Input id="logincode" autoComplete="one-time-code" maxLength={12} required autoFocus
                         value={code} onChange={(e) => setCode(e.target.value)}
                         onPaste={handleCodePaste}
                         className="rounded-sm text-center text-xl tracking-[0.3em] font-mono h-14" data-testid="login-code-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-sm h-11 font-semibold" data-testid="login-code-submit">
                  {loading ? "Checking…" : "Verify & sign in"}
                </Button>
                <Button type="button" variant="outline" disabled={loading || resendIn > 0} onClick={handleResend}
                        className="w-full rounded-sm h-10" data-testid="login-code-resend">
                  {resendIn > 0 ? `Resend code (${resendIn}s)` : "Resend code"}
                </Button>
                <button type="button" className="w-full text-xs text-muted-foreground hover:underline"
                        onClick={() => { setChallenge(null); setCode(""); }} data-testid="login-code-back">
                  ← Back to sign in
                </button>
              </form>
              ) : (
              <form onSubmit={handleLogin} className="space-y-4 mt-6">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                         className="rounded-sm" data-testid="login-email-input" />
                </div>
                <div>
                  <div className="flex items-baseline justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                  <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                         className="rounded-sm" inputTestId="login-password-input" testId="login-password-toggle" />
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-sm h-11 font-semibold"
                        data-testid="login-submit-button">
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
                <div className="relative my-1">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                  <div className="relative flex justify-center"><span className="bg-background px-3 text-[10px] uppercase tracking-wider text-muted-foreground font-mono">or</span></div>
                </div>
                <Button type="button" variant="outline" disabled={loading} onClick={handleGoogleSignIn}
                        className="w-full rounded-sm h-11 font-medium" data-testid="login-google-button">
                  <GoogleGlyph /> Continue with Google
                </Button>
              </form>
              )}
              <p className="text-xs text-muted-foreground mt-6 font-mono">
                Admin? Use the credentials provided to your team.
              </p>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-3 mt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="rname">Contact name *</Label>
                    <Input id="rname" required value={reg.name} onChange={(e) => setR("name", e.target.value)}
                           className="rounded-sm" data-testid="register-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="rbiz">Business name</Label>
                    <Input id="rbiz" value={reg.business_name} onChange={(e) => setR("business_name", e.target.value)}
                           className="rounded-sm" data-testid="register-business-name-input" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="rllc">LLC or trade name</Label>
                  <Input id="rllc" value={reg.llc_name} onChange={(e) => setR("llc_name", e.target.value)}
                         className="rounded-sm" data-testid="register-llc-name-input" />
                </div>
                <div>
                  <Label htmlFor="raddr">Billing address</Label>
                  <AddressFields
                    value={{
                      address: reg.billing_address,
                      city: reg.billing_city,
                      state: reg.billing_state,
                      zip_code: reg.billing_zip,
                    }}
                    onChange={(patch) => setRegMany({
                      billing_address: patch.address ?? reg.billing_address,
                      billing_city: patch.city ?? reg.billing_city,
                      billing_state: patch.state ?? reg.billing_state,
                      billing_zip: patch.zip_code ?? reg.billing_zip,
                    })}
                    showLabels={false}
                    testIdPrefix="register-billing-address"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="rcphone">Contact phone</Label>
                    <Input id="rcphone" type="tel" value={reg.contact_phone} onChange={(e) => setR("contact_phone", e.target.value)}
                           className="rounded-sm" data-testid="register-contact-phone-input" />
                  </div>
                  <div>
                    <Label htmlFor="rbphone">Billing phone</Label>
                    <Input id="rbphone" type="tel" value={reg.billing_phone} onChange={(e) => setR("billing_phone", e.target.value)}
                           className="rounded-sm" data-testid="register-billing-phone-input" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="rcemail">Contact email</Label>
                  <Input id="rcemail" type="email" value={reg.contact_email} onChange={(e) => setR("contact_email", e.target.value)}
                         className="rounded-sm" data-testid="register-contact-email-input" placeholder="where invoices are sent" />
                </div>
                <div>
                  <Label htmlFor="rseconde">Secondary invoice email <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
                  <Input id="rseconde" type="email" value={reg.secondary_invoice_email} onChange={(e) => setR("secondary_invoice_email", e.target.value)}
                         className="rounded-sm" data-testid="register-secondary-invoice-email" placeholder="CC'd on every invoice" />
                </div>
                <div>
                  <Label htmlFor="remail">Portal login email *</Label>
                  <Input id="remail" type="email" required value={reg.email} onChange={(e) => setR("email", e.target.value)}
                         className="rounded-sm" data-testid="register-email-input" placeholder="used to sign in" />
                </div>
                <div>
                  <Label htmlFor="rpass">Password *</Label>
                  <PasswordInput id="rpass" required minLength={6} value={reg.password} onChange={(e) => setR("password", e.target.value)}
                         className="rounded-sm" inputTestId="register-password-input" testId="register-password-toggle" />
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-sm h-11 font-semibold"
                        data-testid="register-submit-button">
                  {loading ? "Creating…" : "Create customer account"}
                </Button>
                <div className="relative my-1">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                  <div className="relative flex justify-center"><span className="bg-background px-3 text-[10px] uppercase tracking-wider text-muted-foreground font-mono">or</span></div>
                </div>
                <Button type="button" variant="outline" disabled={loading} onClick={handleGoogleSignIn}
                        className="w-full rounded-sm h-11 font-medium" data-testid="register-google-button">
                  <GoogleGlyph /> Continue with Google
                </Button>
              </form>
              <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
                Your account is activated instantly — you can add your pools and request deliveries right away.
              </p>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
